import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine"
import { draggable, dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter"
import { getDragIndicator } from "./drag_indicator"
import type { BaseEventPayload, CleanupFn, ElementDragType } from "@atlaskit/pragmatic-drag-and-drop/types"
import { attachInstruction, extractInstruction, Instruction } from "@atlaskit/pragmatic-drag-and-drop-hitbox/list-item"
import { DragLocationHistory, DropTargetLocalizedData } from "@atlaskit/pragmatic-drag-and-drop/dist/types/internal-types"
import { Optional } from "~/types/types.util"

export type DragSourceEvent = BaseEventPayload<ElementDragType>
export type DropTargetEvent = DragSourceEvent & DropTargetLocalizedData

interface Props {
  element: HTMLElement
  onDragStart?: (event: DragSourceEvent) => void
  // onDragEnd is called regardless of whether the drop was successful or not (i.e. not cancelled)
  onDragEnd?: (event: DragSourceEvent) => void
  onGenerateDragPreview?: (event: DragSourceEvent) => void
  // onDrop is only called when a valid drop is called
  onDrop?: (event: DropTargetEvent) => void
}

export default function attachTreeDrag({
  element,
  onDrop,
  onDragEnd,
  onDragStart,
  onGenerateDragPreview,
}: Props): CleanupFn {
  return combine(
    draggable({
      element,
      onDragStart(event) {
        onDragStart?.(event)
      },
      onDrop(event: DragSourceEvent) {
        // I called the function `onDragEnd` because we already have another `onDrop` callback
        onDragEnd?.(event)
      },
      onGenerateDragPreview(event) {
        onGenerateDragPreview?.(event)
      },
    }),
    dropTargetForElements({
      element,
      canDrop({ source, element }) {
        // cannot drop on self
        if (source.element === element) return false
        // only accepting other collections for the time being
        return source.element.hasAttribute("data-node-id")
      },
      getData({ input }) {
        return attachInstruction({}, {
          element,
          input,
          operations: {
            combine: "available",
            "reorder-before": "available",
            "reorder-after": "available",
          },
        })
      },
      onDrag({ self, location }) {
        const instruction = extractInstruction(self.data)
        if (!instruction) {
          removeDragIndicator(self.element)
          return
        }

        // Only handle drop if on the deepest element since we can have nested lists
        if (!isDeepestDropTarget(self.element, location)) return

        // No need to do anything, already have a drop indicator in the right spot
        const indicator = getExistingDragIndicator(self.element)
        if (indicator?.getAttribute("data-operation") === instruction.operation) return

        // Get rid of any old drag indicator
        removeDragIndicator(self.element)

        // Add the new drag indicator
        addDragIndicator(self.element, instruction)
      },
      onDragLeave({ self }) {
        removeDragIndicator(self.element)
      },
      onDrop({ self, source, location }) {
        removeDragIndicator(self.element)

        const instruction = extractInstruction(self.data)
        if (!instruction) return

        // Only handle the drop if on the deepest drop target since we can have multiple drop targets due to nested
        // lists
        if (!isDeepestDropTarget(self.element, location)) return

        const toMove = source.element
        if (instruction.operation === "combine") {
          const ul = self.element.querySelector("ul")
          ul?.prepend(toMove)
        } else {
          self.element.insertAdjacentElement(
            instruction.operation === "reorder-before" ? "beforebegin" : "afterend",
            toMove,
          )
        }

        onDrop?.({ self, source, location })
      },
    }))
}

function addDragIndicator(element: Element, instruction: Instruction) {
  if (instruction.operation === "combine") {
    element.classList.add("combine-indicator")
  } else {
    const indicator = getDragIndicator({ instruction })
    element.insertAdjacentElement("beforeend", indicator)
  }
}

function removeDragIndicator(element: Element) {
  // For "combine" operations - just an outline
  element.classList.remove("combine-indicator")

  // For "reorder" operations - a separate nested element
  getExistingDragIndicator(element)?.remove()
}

function getExistingDragIndicator(element: Element): Optional<Element> {
  return element.querySelector(".drag-indicator")
}

function isDeepestDropTarget(element: Element, location: DragLocationHistory): boolean {
  const dropTargets = location.current.dropTargets
  return dropTargets.length > 0 && dropTargets[0].element === element
}
